import { Dispatch } from 'redux';
import { ApiClient } from '../api.client';
import { ReduxStore } from '../payload-types';
import { API_DATE_FORMAT, API_KEYS, LINKS, PAYMENT_METHOD_MAPPING, POLICY_PERMISSIONS } from '@trip-ux/constants';
import { dayjs } from '@trip-ux/common';
import { find, first, get, isEmpty, map, omit, pick, toNumber, has, orderBy, eq, gt, toUpper, isNil } from 'lodash';
import { getDefaultCountryOfResidence, getLabels } from '../request.mappers';
import {
  EndorsementPayment,
  ENDORSEMENT_PAYMENT_DETAILS,
  ENDORSEMENT_INITIATE_TOKEN,
  ENDORSEMENT_PAYMENT_METHOD,
  POLICY_UPDATE_START,
  POLICY_UPDATE_SUCCESS,
  POLICY_UPDATE_FAILED,
  POLICY_RETRY_START,
  POLICY_RETRY_SUCCESS,
  POLICY_RETRY_FAILED,
  SHOW_APPLY_CHANGES,
  RESET_QUOTE,
  RE_QUOTE_START,
  RE_QUOTE_SUCCESS,
  RE_QUOTE_FAILED
} from './types';
import { abs } from 'mathjs';
import { TripInformation } from '../quote';
import { getDiscounts } from '../utils';
import { PAYMENT_TYPE } from '../traveller-payments';

export const setShowApplyChanges =
  (payload: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: SHOW_APPLY_CHANGES,
      payload
    });
  };

export const endorsementInitiateToken =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: ENDORSEMENT_INITIATE_TOKEN
    });
  };

export const setEndorsementPayment =
  (payload: EndorsementPayment) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: ENDORSEMENT_PAYMENT_DETAILS,
      payload
    });
  };

export const applyChanges =
  (payload: unknown) =>
  (
    dispatch: Dispatch,
    getState: () => ReduxStore,
    apiClient: ApiClient
  ): Promise<void | { type: string; payload: unknown }> => {
    dispatch({ type: POLICY_UPDATE_START });
    return apiClient(dispatch, getState)
      .put(LINKS.POLICY, prepareApply(getState()))
      .then(data => {
        dispatch({ type: POLICY_UPDATE_SUCCESS, payload: { ...data, caller: payload } });
      })
      .catch(e => dispatch({ type: POLICY_UPDATE_FAILED, payload: { e } }));
  };

export const retryChange =
  (payload: unknown) =>
  (dispatch: Dispatch, getState: () => ReduxStore, apiClient: ApiClient): Promise<void | { payload: unknown }> => {
    dispatch({ type: POLICY_RETRY_START });
    return apiClient(dispatch, getState)
      .put(LINKS.POLICY_RETRY, prepareRetry(getState()))
      .then(data => {
        dispatch({ type: POLICY_RETRY_SUCCESS, payload: data });
      })
      .catch(e => dispatch({ type: POLICY_RETRY_FAILED, payload: { e } }));
  };

const checkNumber = val => (val ? toNumber(val) : val);
export const requote =
  (changeRequest: [string]) =>
  (
    dispatch: Dispatch,
    getState: () => ReduxStore,
    apiClient: ApiClient
  ): Promise<void | { type: string; payload: unknown }> => {
    dispatch({ type: RE_QUOTE_START });
    return apiClient(dispatch, getState)
      .post(LINKS.RE_QUOTE, prepareDeltaQuote(getState()))
      .then(data => {
        dispatch({ type: RE_QUOTE_SUCCESS, payload: data });
      })
      .catch(e => dispatch({ type: RE_QUOTE_FAILED, payload: { changeRequest, e } }));
  };

export const resetQuote =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: RESET_QUOTE
    });
  };

const prepareApply = (store: ReduxStore) => {
  const hasDelta = store.policyUpdate.deltaTotal !== 0;
  const { paxes, ...restDelta } = prepareDeltaQuote(store);
  const changeDelta = prepareChanges(store);
  return {
    ...restDelta,
    paxes: map(paxes, pax => ({
      changeDelta,
      ...pax,
      ...(pax.policyStatus === 'ACTIVE'
        ? hasDelta && { payments: prepareDeltaPayments(store, pax.policyNumber) }
        : { payments: prepareReinstatePayments(store, pax.policyNumber) })
    }))
  };
};

const prepareRetry = (store: ReduxStore) => {
  const selectedPolicies = store.policyInfo.selectedPolicies;
  const { paxes } = first(selectedPolicies);
  const { policyNumber } = first(paxes);
  return { policyNumber };
};

const prepareCharge = (deltaPax, endorsementPayment, recentPayment, policyPax) => {
  const isTokenorCC =
    endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.TOKEN ||
    endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.CREDIT_CARD;
  return [
    {
      amount: deltaPax.deltaAmount,
      type: API_KEYS.CHARGE,
      method: isTokenorCC
        ? PAYMENT_METHOD_MAPPING[endorsementPayment.ccType]
        : endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.ORIGINAL
        ? recentPayment.method
        : endorsementPayment.paymentMethod,
      ...(endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.ORIGINAL && {
        cardReference: recentPayment.cardReference
      }),
      ...(endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.AGENCY && {
        cardReference: ENDORSEMENT_PAYMENT_METHOD.AGENCY
      }),
      ...(isTokenorCC && {
        details: {
          cardOwnerReferenceNumber: policyPax.referenceNumber,
          dataKey: endorsementPayment.token,
          paymentType:
            endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.CREDIT_CARD ? 'CREDIT_CARD' : 'TOKEN',
          cardHolderName: endorsementPayment.cardHolderName,
          ...(endorsementPayment.paymentMethod === ENDORSEMENT_PAYMENT_METHOD.CREDIT_CARD && {
            expDate: endorsementPayment.expiryDate,
            cvd: endorsementPayment.cvv,
            paymentType: PAYMENT_TYPE.CARD
          })
        }
      })
    }
  ];
};

const prepareRefundOriginal = (deltaPax, endorsementPayment, recentPayment) => {
  const amount = abs(deltaPax.deltaAmount);
  return [
    {
      amount,
      type: API_KEYS.REFUND,
      method: endorsementPayment.paymentMethod,
      cardReference: recentPayment.cardReference
    }
  ];
};

export const prepareDeltaPayments = (store: ReduxStore, policyNumber: string) => {
  const endorsementPayment = store.policyUpdate.endorsementPayment;
  const selectedPolicies = store.policyInfo.selectedPolicies;
  const deltaPaxes = store.policyUpdate.deltaPaxes;

  const selectedPolicy = find(selectedPolicies, policy => first(policy.paxes).policyNumber === policyNumber);
  const policyPax = first(selectedPolicy.paxes);
  const originalPayments = orderBy(policyPax.payments, payment => payment.paymentDate, ['asc']);
  const recentPayment = first(originalPayments);
  const deltaPax = find(deltaPaxes, { policyNumber });
  if (eq(deltaPax.deltaAmount, 0)) {
    return [];
  }
  // Charge
  if (gt(deltaPax.deltaAmount, 0)) {
    return prepareCharge(deltaPax, endorsementPayment, recentPayment, policyPax);
  }

  // Refund to original payment methods
  return prepareRefundOriginal(deltaPax, endorsementPayment, recentPayment);
};

export const prepareReinstatePayments = (store: ReduxStore, policyNumber: string) => {
  const selectedPolicies = store.policyInfo.selectedPolicies;
  const selectedPolicy = find(selectedPolicies, policy => first(policy.paxes).policyNumber === policyNumber);
  const policyPax = first(selectedPolicy.paxes);
  const planAmount = policyPax.plan.premiumTotal;
  const originalPayments = orderBy(policyPax.payments, payment => payment.paymentDate, ['asc']);
  const recentPayment = first(originalPayments);
  const deltaAmountPax = [{ policyNumber: policyNumber, deltaAmount: planAmount }];
  const endorsementPayment = {
    paymentMethod: 'ORIGINAL',
    paymentAmount: planAmount,
    tokenState: 'NOT_STARTED'
  };
  const deltaPax = find(deltaAmountPax, { policyNumber });
  if (eq(deltaPax.deltaAmount, 0)) {
    return [];
  }
  // Charge
  if (gt(deltaPax.deltaAmount, 0)) {
    return prepareCharge(deltaPax, endorsementPayment, recentPayment, policyPax);
  }
  return [];
};

const getSchoolDates = (tripInformation: TripInformation, dateFormat: string) => {
  return {
    ...(!isEmpty(tripInformation.educationStartDate) && {
      educationStartDate: dayjs(tripInformation.educationStartDate, dateFormat).format(API_DATE_FORMAT)
    }),
    ...(!isEmpty(tripInformation.educationEndDate) && {
      educationEndDate: dayjs(tripInformation.educationEndDate, dateFormat).format(API_DATE_FORMAT)
    }),
    ...(!isEmpty(tripInformation.leavingDate) && {
      leavingDate: dayjs(tripInformation.leavingDate, dateFormat).format(API_DATE_FORMAT)
    }),
    ...(!isEmpty(tripInformation.tripStartDate) && {
      tripStartDate: dayjs(tripInformation.tripStartDate, dateFormat).format(API_DATE_FORMAT)
    })
  };
};

const prepareDeltaQuote = (store: ReduxStore) => {
  const selectedPolicies = store.policyInfo.selectedPolicies;
  const { paxes, tripInformation: rawTripInformation, createdAt, ...rest } = first(selectedPolicies);
  const isSuperUser = store.userProfile.claims.includes(POLICY_PERMISSIONS.POLICY_ADMIN);
  const tripInformation = omit(rawTripInformation, 'effectiveDate');
  const hasleavingDate = !isNil(get(rawTripInformation, 'leavingDate'));
  const changes = store.policyInfo.deltaChanges;
  const planAdjustments = get(first(paxes), 'plan.adjustments');
  // @ts-ignore
  const discounts = getDiscounts(planAdjustments);
  const labels = getLabels(store.config, store.tms);
  const dateFormat = labels.policy_search.datePickerLabels.dateFormat;

  // For date fields, we need to check if user made a change.
  // If changed: parse from user's display format as UTC to preserve the calendar date without timezone shifting
  // If NOT changed: use dayjs.utc() to parse original date to prevent timezone shifting
  const tripBookingDateChanged = get(changes, 'tripInformation.tripBookingDate');
  const tripBookingDate = tripBookingDateChanged
    ? dayjs.utc(tripBookingDateChanged as string, dateFormat).format()
    : dayjs.utc(get(first(paxes), 'tripBookingDate')).format();

  let applicationDate = null;
  const applicationDateChanged = get(changes, 'tripInformation.applicationDate');
  if (applicationDateChanged) {
    // applicationDate is a real timestamp displayed via dayjsLocal(), so parse as local time
    // to preserve the user's intended calendar date through the display round-trip.
    // Unlike tripBookingDate (date-only/UTC midnight), applicationDate carries timezone context.
    applicationDate = dayjs(applicationDateChanged as string, dateFormat).format();
  }

  // For startDate, endDate, etc. - use dayjs.utc() for original policy values to prevent timezone shifting
  const startDateChanged = get(changes, 'tripInformation.startDate');
  const startDate = startDateChanged
    ? dayjs(startDateChanged as string, dateFormat).format(API_DATE_FORMAT)
    : dayjs.utc(rawTripInformation.startDate).format(API_DATE_FORMAT);

  const endDateChanged = get(changes, 'tripInformation.endDate');
  const endDate = endDateChanged
    ? dayjs(endDateChanged as string, dateFormat).format(API_DATE_FORMAT)
    : dayjs.utc(rawTripInformation.endDate).format(API_DATE_FORMAT);

  const arrivalDateChanged = get(changes, 'tripInformation.arrivalDate');
  const tripStartDate = arrivalDateChanged
    ? dayjs(arrivalDateChanged as string, dateFormat).format(API_DATE_FORMAT)
    : dayjs.utc(rawTripInformation.tripStartDate ?? rawTripInformation.startDate).format(API_DATE_FORMAT);

  const leavingDateChanged = get(changes, 'tripInformation.leavingDate');
  let leavingDate: string | undefined;
  if (leavingDateChanged) {
    leavingDate = dayjs(leavingDateChanged as string, dateFormat).format(API_DATE_FORMAT);
  } else if (hasleavingDate) {
    leavingDate = dayjs.utc(rawTripInformation.leavingDate).format(API_DATE_FORMAT);
  }

  const countryOfResidence = getDefaultCountryOfResidence(null, null, tripInformation.countryOfResidence);
  const { policyNumber: familyPolicyNumber } = first(paxes);

  return {
    ...rest,
    manulifeEmployeeRelationship: get(discounts, 'manulifeEmployeeRelationship'),
    hasCompanionDiscount: get(discounts, 'hasCompanionDiscount'),
    createdAt: dayjs(createdAt).format(),
    ...(isSuperUser && applicationDate && { applicationDate }),
    tripInformation: {
      ...tripInformation,
      startDate,
      effectiveDate: startDate,
      endDate,
      tripBookingDate,
      ...getSchoolDates(tripInformation, dateFormat),
      countryOfResidence,
      tripStartDate,
      ...(hasleavingDate && { leavingDate })
    },
    paxes: map(selectedPolicies, policy => {
      const { plan, contact, age, policyNumber, dateOfBirth, ...restOfPax } = first(policy.paxes);
      const deductibleField = `${policyNumber}.deductible`;
      const planDeductible = get(plan, 'tripPreference.deductible');
      const newDeductible = checkNumber(get(changes, deductibleField, planDeductible));
      // For dateOfBirth: if changed, parse from display format; if not, use dayjs.utc() to prevent timezone shifting
      const dobChanged = get(changes, `${policyNumber}.dateOfBirth`);
      const dob = dobChanged
        ? dayjs(dobChanged as string, dateFormat).format(API_DATE_FORMAT)
        : dayjs.utc(dateOfBirth).format(API_DATE_FORMAT);
      const newAge = get(changes, `${policyNumber}.age`, age);
      const daysPerTrip = checkNumber(get(changes, `${policyNumber}.daysPerTrip`, plan.tripPreference.daysPerTrip));
      return {
        ...omit(restOfPax, ['payments']),
        ...pick(get(changes, policyNumber, {}), ['firstName', 'lastName']),
        policyNumber,
        dateOfBirth: dob,
        age: newAge,
        contact: {
          ...contact,
          ...pick(get(changes, policyNumber, {}), ['email', 'phone']),
          address: {
            ...contact.address,
            ...pick(get(changes, policyNumber, {}), ['addressLine1', 'addressLine2', 'city', 'postalCode'])
          }
        },
        plan: {
          code: plan.code,
          ...(!isNil(newDeductible) && {
            adjustments: [
              {
                type: API_KEYS.DEDUCTIBLE,
                value: newDeductible
              }
            ]
          }),
          optionalBenefits: has(changes, 'riders.selected')
            ? map(get(changes, 'riders.selected') as string[], code => ({ code }))
            : map(plan.optionalBenefits, ({ code }) => ({ code })),

          tripPreference: {
            tripCost: checkNumber(get(changes, `${policyNumber}.tripCost`, plan.tripPreference.tripCost)),
            coverageAmount: checkNumber(
              get(changes, `${policyNumber}.coverageAmount`, plan.tripPreference.coverageAmount)
            ),
            ...(!isNil(daysPerTrip) && { daysPerTrip: toNumber(daysPerTrip) })
          }
        },
        tripBookingDate
      };
    }),
    dependents: map(get(store, 'policyInfo.dependentsChanges'), dependentChanges => ({
      ...dependentChanges,
      dateOfBirth: dayjs.utc(dependentChanges.dateOfBirth).format(API_DATE_FORMAT),
      relationship: toUpper(dependentChanges.relationship),
      tripBookingDate,
      tripCost: checkNumber(get(changes, `${familyPolicyNumber}.tripCost`, null))
    }))
  };
};

const prepareChanges = (store: ReduxStore) => {
  const theFieldChanges = store.policyInfo.deltaChanges;
  const theInitials = store.policyInfo.initialValues;
  const changeDelta = [];
  let chgKeys = Object.keys(theFieldChanges[store.policyInfo.selectedPolicies[0].paxes[0].policyNumber]);
  chgKeys.forEach(theKey => {
    changeDelta.push({
      fieldChanged: theKey,
      to: theFieldChanges[store.policyInfo.selectedPolicies[0].paxes[0].policyNumber][theKey],
      from: theInitials[store.policyInfo.selectedPolicies[0].paxes[0].policyNumber][theKey] || ''
    });
  });
  if (theFieldChanges.tripInformation) {
    chgKeys = Object.keys(theFieldChanges.tripInformation);
    chgKeys.forEach(theKey => {
      changeDelta.push({
        fieldChanged: theKey,
        to: theFieldChanges.tripInformation[theKey],
        from: theInitials.tripInformation[theKey]
      });
    });
  }

  if (theFieldChanges.riders && theFieldChanges.riders['selected']) {
    changeDelta.push({
      fieldChanged: 'optionalBenefits',
      to: theFieldChanges.riders['selected'],
      from: theInitials.riders['selected']
    });
  }

  if (store.policyInfo.dependentDelta?.hasChanges) {
    changeDelta.push({
      fieldChanged: 'customers',
      to: store.policyInfo.dependentsChanges,
      from: store.policyInfo.dependentsInitials
    });
  }

  return changeDelta;
};
